import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Network, Loader2, Lock, Mail, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { errMsg } from "@/lib/api";
import { LOGIN } from "@/constants/testIds";

export default function Login() {
  const { user, login } = useAuth();
  const { isDark, toggle } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative flex items-center justify-center px-4 overflow-hidden">
      <div className="absolute inset-0 grid-lines pointer-events-none opacity-60" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-neon/10 blur-3xl pointer-events-none" />

      <button
        onClick={toggle}
        className="absolute top-5 right-5 w-9 h-9 grid place-items-center rounded-md border border-line bg-panel text-dim hover:text-neon hover:border-neon transition-colors"
        title={isDark ? "Light mode" : "Dark mode"}
        data-testid="theme-toggle-btn"
      >
        {isDark ? <Sun size={16} /> : <Moon size={16} />}
      </button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="relative w-full max-w-md"
      >
        <div className="flex items-center justify-center gap-2 mb-7">
          <div className="w-10 h-10 grid place-items-center rounded-lg bg-neon text-white neon-glow">
            <Network size={20} />
          </div>
          <span className="font-heading text-2xl tracking-tight">
            789<span className="text-neon">PROXY</span>
          </span>
        </div>

        <div className="card-panel p-7">
          <div className="text-center mb-6">
            <h2 className="font-heading text-xl tracking-tight">Sign in to your account</h2>
            <p className="text-sm text-dim mt-1">Proxy Marketplace &amp; Gateway</p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="overline mb-1.5 block">Email</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-dim" />
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@789proxy.com"
                  className="h-11 pl-9 bg-black border-line font-mono text-sm rounded-md focus-visible:ring-neon"
                  data-testid={LOGIN.emailInput}
                />
              </div>
            </div>

            <div>
              <label className="overline mb-1.5 block">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-dim" />
                <Input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 pl-9 bg-black border-line font-mono text-sm rounded-md focus-visible:ring-neon"
                  data-testid={LOGIN.passwordInput}
                />
              </div>
            </div>

            {error && (
              <div
                className="rounded-md border border-signal/50 bg-signal/10 text-signal font-mono text-xs px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-md bg-neon text-white hover:bg-neon/90 font-medium text-sm tracking-wide"
              data-testid={LOGIN.submitButton}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign in →"}
            </Button>
          </form>
        </div>

        <p className="text-center font-mono text-[10px] text-dim tracking-widest uppercase mt-6">
          789PROXY · restricted access
        </p>
      </motion.div>
    </div>
  );
}
